import { z } from "zod";
import {
  cvcLengthValid,
  digitsOnlyPan,
  expectedCvcLength,
  isExpiryNotPast,
  parseExpiryMmYy,
  passesLuhn,
} from "@/src/lib/utils/card-validation";

export const checkoutFormSchema = z
  .object({
    // Personal information
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Valid email is required"),
    phone: z.string().optional(),

    // Shipping address information
    address1: z.string().min(1, "Address is required"),
    address2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    state: z.string().min(1, "State/Province is required"),
    postal: z.string().min(1, "Postal code is required"),
    country: z.string().min(2, "Country is required"),

    // Billing address toggle
    sameBillingAddress: z.boolean().default(true),

    // Billing address information (optional by default, validated conditionally)
    billingFirstName: z.string().optional(),
    billingLastName: z.string().optional(),
    billingAddress: z.string().optional(),
    billingCountry: z.string().optional(),
    billingState: z.string().optional(),
    billingCity: z.string().optional(),
    billingZip: z.string().optional(),

    // Payment method selection
    paymentMethod: z.enum(["card", "apple-pay"]).default("card"),

    // Card information (required only when paymentMethod is "card")
    cardNumber: z.string().optional(),
    expiryDate: z.string().optional(),
    cvc: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.paymentMethod !== "card") return;

    const pan = digitsOnlyPan(data.cardNumber ?? "");
    const expiry = (data.expiryDate ?? "").trim();
    const cvc = data.cvc ?? "";

    if (pan.length < 13 || pan.length > 19 || !/^\d+$/.test(pan)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter a valid card number (13–19 digits)",
        path: ["cardNumber"],
      });
    } else if (!passesLuhn(pan)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Card number does not look valid",
        path: ["cardNumber"],
      });
    }

    if (!parseExpiryMmYy(expiry)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Use MM/YY format",
        path: ["expiryDate"],
      });
    } else if (!isExpiryNotPast(expiry)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Card has expired",
        path: ["expiryDate"],
      });
    }

    const { min, max } = expectedCvcLength(pan);
    if (!cvcLengthValid(cvc, pan)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          min === 4
            ? "Enter the 4-digit security code on the front of the card"
            : "Enter the 3-digit security code on the back of the card",
        path: ["cvc"],
      });
    }
  })
  .refine(
    (data) => {
      // If billing address is different, validate billing fields
      if (!data.sameBillingAddress) {
        return (
          data.billingFirstName &&
          data.billingFirstName.length > 0 &&
          data.billingLastName &&
          data.billingLastName.length > 0 &&
          data.billingAddress &&
          data.billingAddress.length > 0 &&
          data.billingCountry &&
          data.billingCountry.length >= 2 &&
          data.billingState &&
          data.billingState.length > 0 &&
          data.billingCity &&
          data.billingCity.length > 0 &&
          data.billingZip &&
          data.billingZip.length > 0
        );
      }
      return true;
    },
    {
      message: "All billing address fields are required when using a different billing address",
      path: ["billingAddress"],
    }
  );

export type CheckoutFormData = z.infer<typeof checkoutFormSchema>;
